import React, { useState } from 'react';
import { isUntrustedAccountLogoUrl } from '@/lib/marketExperience';
import { Badge } from '@/components/ui/badge';
import { getDeadlineHorizon, getDeadlineHorizonColor } from '@/utils/procurementDealUtils';
import { dealDeadline, formatDeadline, daysUntilDeadline } from '@/lib/homeDealTasks';
import { fetchFitLatest } from '@/lib/api/fit';
import Hint from './pilotHint';

/* Fit rationale per deal, fetched on first hover (≈0.4 s) and kept for the session: the bulk fit
 * call that paints the badge carries only the label, never the reasoning. */
const fitReasonCache = new Map();
const fitDone = new Set();

/** Same colours as LiveRadarList's fit light badge. */
const FIT_LIGHT_CLASSES = {
  'Strong Fit': 'bg-green-100 text-green-800 border-green-200',
  'Moderate Fit': 'bg-amber-100 text-amber-800 border-amber-200',
  'Weak Fit': 'bg-orange-100 text-orange-800 border-orange-200',
  'No Fit': 'bg-red-100 text-red-800 border-red-200',
};

export function FitLightBadge({ label, tenantId, dealId }) {
  const [reason, setReason] = useState(() => (dealId ? fitReasonCache.get(dealId) : null) || null);
  if (!label) return null;
  const badge = (
    <Badge variant="outline" className={`text-[10px] h-5 px-1.5 shrink-0 whitespace-nowrap border ${FIT_LIGHT_CLASSES[label] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {label}
    </Badge>
  );
  if (!tenantId || !dealId) return badge;
  const load = (open) => {
    if (!open || fitReasonCache.has(dealId)) return;
    fitReasonCache.set(dealId, '');
    fetchFitLatest(tenantId, dealId)
      .then((full) => {
        const text = full?.light?.recommendation || '';
        fitReasonCache.set(dealId, text);
        fitDone.add(dealId);
        setReason(text || ' ');
      })
      .catch(() => { fitReasonCache.delete(dealId); fitDone.add(dealId); setReason(' '); });
  };
  const content = (
    <>
      <p className="mb-1 font-semibold text-[#00C2C2]">Why {label}</p>
      <p>{(reason && reason.trim()) || fitReasonCache.get(dealId) || (fitDone.has(dealId) ? 'No rationale recorded for this deal.' : 'Loading…')}</p>
    </>
  );
  return <Hint content={content} onOpenChange={load}>{badge}</Hint>;
}

/** Deadline date in the Live Radar horizon colour (0–7 red, 8–30 amber, 31–90 teal). */
export function DeadlineBadge({ deal }) {
  const dl = dealDeadline(deal);
  const horizon = getDeadlineHorizon(dl);
  const days = daysUntilDeadline(deal);
  const hint = days == null ? 'No deadline published'
    : days < 0 ? `Closed ${-days} day${days === -1 ? '' : 's'} ago`
      : days === 0 ? 'Closes today' : `Closes in ${days} day${days === 1 ? '' : 's'}`;
  return (
    <Hint content={hint}>
      <Badge variant="outline" className={`text-[10px] h-5 px-1.5 shrink-0 whitespace-nowrap border ${getDeadlineHorizonColor(horizon.label)}`}>
        {formatDeadline(dl)}
      </Badge>
    </Hint>
  );
}

/**
 * Account logo in the TopAccountsCard style (32 px, rounded, bordered, white). Falls back to
 * `fallback` when there is no trusted URL or the image fails to load.
 */
export function AccountLogo({ url, name, fallback }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed || isUntrustedAccountLogoUrl(url)) return fallback;
  return (
    <img
      src={url}
      alt={name || ''}
      onError={() => setFailed(true)}
      className="h-8 w-8 flex-shrink-0 rounded-md border bg-white object-contain p-0.5"
    />
  );
}
